import { Injectable } from '@angular/core';
import { QueryEntity } from '@examdojo/state';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PayoutRateStoreModel, PayoutRateUIModel } from './payout-rates.model';
import { PayoutRateState, PayoutRateStore } from './payout-rates.store';

@Injectable({
  providedIn: 'root',
})
export class PayoutRateQuery extends QueryEntity<PayoutRateState, PayoutRateStoreModel, PayoutRateUIModel, 'id'> {
  constructor(protected override readonly store: PayoutRateStore) {
    super(
      store,
      () => [],
      () => [],
      (entity: PayoutRateStoreModel) => entity,
    );
  }

  selectUserRate(userId: string): Observable<PayoutRateUIModel | null> {
    return this.entities$.pipe(
      map((rates) => rates.find((rate) => rate.user_id === userId && rate.valid_to === null) || null),
    );
  }

  selectAllRates(): Observable<PayoutRateUIModel[]> {
    return this.entities$;
  }

  selectUserRates(userId: string): Observable<PayoutRateUIModel[]> {
    return this.entities$.pipe(
      map((rates) =>
        rates
          .filter((rate) => rate.user_id === userId)
          .sort((a, b) => {
            // If valid_from is NULL, treat as oldest (negative infinity)
            const aValidFrom = a.valid_from ? new Date(a.valid_from).getTime() : -Infinity;
            const bValidFrom = b.valid_from ? new Date(b.valid_from).getTime() : -Infinity;
            return bValidFrom - aValidFrom;
          }),
      ),
    );
  }

  getUserRate(userId: string): PayoutRateUIModel | null {
    return this.getAllUIEntities().find((rate) => rate.user_id === userId && rate.valid_to === null) || null;
  }

  getUserRates(userId: string): PayoutRateUIModel[] {
    return this.getAllUIEntities()
      .filter((rate) => rate.user_id === userId)
      .sort((a, b) => {
        // If valid_from is NULL, treat as oldest (negative infinity)
        const aValidFrom = a.valid_from ? new Date(a.valid_from).getTime() : -Infinity;
        const bValidFrom = b.valid_from ? new Date(b.valid_from).getTime() : -Infinity;
        return bValidFrom - aValidFrom;
      });
  }

  getRateForDate(userId: string, date: Date): PayoutRateUIModel | null {
    const rates = this.getUserRates(userId);
    const dateTime = date.getTime();

    return (
      rates.find((rate) => {
        // If valid_from is NULL, it applies backwards indefinitely (negative infinity)
        const validFrom = rate.valid_from ? new Date(rate.valid_from).getTime() : -Infinity;
        const validTo = rate.valid_to ? new Date(rate.valid_to).getTime() : Infinity;
        return dateTime >= validFrom && dateTime < validTo;
      }) || null
    );
  }

  getAllRates(): PayoutRateUIModel[] {
    return this.getAllUIEntities();
  }
}
